import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../utils/axiosConfig';
import '../styles/EmlakPortfoy.css';
import { useTranslation } from 'react-i18next';

interface EmlakFotograf {
  fotoID: number;
  portfoyID: number;
  dosyaYolu: string;
  isVitrin: boolean;
}

interface EmlakIletisim {
  iletisimID: number;
  portfoyID: number;
  adSoyad: string;
  telefon?: string;
  eposta?: string;
}

interface EmlakOzellik {
  ozellikID: number;
  portfoyID: number;
  ozellikAdi: string;
}

interface EmlakPortfoy {
  portfoyID: number;
  baslik: string;
  adres?: string;
  aciklama?: string;
  fiyat: number;
  m2: number;
  ilanNo: string;
  tur: string;
  durum: string;
  eklenmeTarihi: string;
  aktifMi: boolean;
  iletisimler: EmlakIletisim[];
  ozellikler: EmlakOzellik[];
  fotograflar: EmlakFotograf[];
}

const API_BASE = '';

const turLabels: Record<string, string> = {
  'Konut': 'Konut',
  'IsYeri': 'İş Yeri',
  'Arsa': 'Arsa',
  'Tarla': 'Tarla'
};

const durumLabels: Record<string, string> = {
  'Satilik': 'Satılık',
  'Kiralik': 'Kiralık',
  'Portfoy': 'Portföy'
};

const turIcons: Record<string, string> = {
  'Konut': 'fa-home', 'IsYeri': 'fa-briefcase', 'Arsa': 'fa-map', 'Tarla': 'fa-seedling'
};

const EmlakPortfoyListesi: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [portfoyler, setPortfoyler] = useState<EmlakPortfoy[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filterTurler, setFilterTurler] = useState<string[]>([]);
  const [filterDurumlar, setFilterDurumlar] = useState<string[]>([]);
  const [filterAktif, setFilterAktif] = useState<string>('aktif');
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [quickViewIndex, setQuickViewIndex] = useState<number | null>(null);
  const [qvActivePhoto, setQvActivePhoto] = useState(0);
  const [turDropdownOpen, setTurDropdownOpen] = useState(false);
  const [durumDropdownOpen, setDurumDropdownOpen] = useState(false);
  const turDropdownRef = useRef<HTMLDivElement>(null);
  const durumDropdownRef = useRef<HTMLDivElement>(null);
  const itemsPerPage = 12;

  useEffect(() => {
    fetchPortfoyler();
  }, []);

  // Dropdown dışına tıklama ile kapatma
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (turDropdownRef.current && !turDropdownRef.current.contains(e.target as Node)) setTurDropdownOpen(false);
      if (durumDropdownRef.current && !durumDropdownRef.current.contains(e.target as Node)) setDurumDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchPortfoyler = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/EmlakPortfoy');
      setPortfoyler(response.data);
    } catch (error) {
      toast.error(t('msg.islemHatasi'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await axiosInstance.delete(`/api/EmlakPortfoy/${id}`);
      toast.success(t('msg.islemBasarili'));
      setDeleteConfirm(null);
      fetchPortfoyler();
    } catch (error) {
      toast.error(t('msg.islemHatasi'));
    }
  };

  const getVitrinFoto = (portfoy: EmlakPortfoy): string | null => {
    const vitrin = portfoy.fotograflar.find(f => f.isVitrin);
    if (vitrin) return vitrin.dosyaYolu;
    if (portfoy.fotograflar.length > 0) return portfoy.fotograflar[0].dosyaYolu;
    return null;
  };

  const toggleFilter = (arr: string[], value: string, setter: (v: string[]) => void) => {
    setter(arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]);
  };

  const filtered = portfoyler.filter(p => {
    const matchSearch = !searchText ||
      p.baslik.toLowerCase().includes(searchText.toLowerCase()) ||
      p.ilanNo.toLowerCase().includes(searchText.toLowerCase()) ||
      (p.adres && p.adres.toLowerCase().includes(searchText.toLowerCase()));
    const matchTur = filterTurler.length === 0 || filterTurler.includes(p.tur);
    const matchDurum = filterDurumlar.length === 0 || filterDurumlar.includes(p.durum);
    const matchAktif = filterAktif === '' || (filterAktif === 'aktif' ? p.aktifMi : !p.aktifMi);
    return matchSearch && matchTur && matchDurum && matchAktif;
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedData = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, filterTurler, filterDurumlar, filterAktif]);

  const fotoUrl = (dosyaYolu: string) => `${axiosInstance.defaults.baseURL}/api/EmlakPortfoy/foto/${dosyaYolu}`;

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const openQuickView = (portfoyId: number) => {
    const idx = filtered.findIndex(p => p.portfoyID === portfoyId);
    if (idx >= 0) {
      setQuickViewIndex(idx);
      setQvActivePhoto(0);
    }
  };

  const qvPortfoy = quickViewIndex !== null ? filtered[quickViewIndex] : null;

  const qvNavigate = (dir: number) => {
    if (quickViewIndex === null) return;
    const newIdx = quickViewIndex + dir;
    if (newIdx >= 0 && newIdx < filtered.length) {
      setQuickViewIndex(newIdx);
      setQvActivePhoto(0);
    }
  };

  return (
    <div className="ep-container">
      {/* Header */}
      <div className="ep-header">
        <div className="ep-header-left">
          <h1 className="ep-title">{t('emlakPortfoy.baslik', 'Emlak Portföyü')}</h1>
          <p style={{ fontSize: '13.5px', color: '#64748b', margin: 0 }}>{t('emlakPortfoy.aciklama', 'Satılık ve kiralık tüm gayrimenkul ilanlarınızı detaylı olarak takip edin.')}</p>
        </div>
        <button className="ep-btn-primary" onClick={() => navigate('/emlak-portfoy/yeni')}>
          <i className="fas fa-plus"></i> {t('btn.yeniIlan', 'Yeni İlan')}
        </button>
      </div>

      {/* Filters */}
      <div className="ep-filters">
        <div className="ep-search-wrapper">
          <span className="ep-search-icon">
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </span>
          <input
            type="text"
            className="ep-search"
            placeholder={t('label.ara')}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
        </div>

        {/* Tür Checkbox Dropdown */}
        <div className="ep-filter-dropdown" ref={turDropdownRef}>
          <button className={`ep-filter-dropdown-btn ${filterTurler.length > 0 ? 'has-selection' : ''}`} onClick={() => { setTurDropdownOpen(!turDropdownOpen); setDurumDropdownOpen(false); }}>
            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22V12h6v10"/><path d="M8 6h.01M12 6h.01M16 6h.01"/></svg>
            {filterTurler.length === 0 ? t('emlak.tumTurler', 'Tüm Türler') : `${filterTurler.length} ${t('emlak.turSecili', 'Tür Seçili')}`}
            <svg className={`ep-filter-arrow ${turDropdownOpen ? 'open' : ''}`} width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
          {turDropdownOpen && (
            <div className="ep-filter-dropdown-menu">
              {Object.entries(turLabels).map(([key, label]) => (
                <label key={key} className="ep-filter-checkbox-item">
                  <input type="checkbox" checked={filterTurler.includes(key)} onChange={() => toggleFilter(filterTurler, key, setFilterTurler)} />
                  <span className="ep-filter-checkmark"></span>
                  {t('emlak.tur.' + key, label)}
                </label>
              ))}
              {filterTurler.length > 0 && (
                <button className="ep-filter-clear-btn" onClick={() => setFilterTurler([])}>{t('btn.temizle')}</button>
              )}
            </div>
          )}
        </div>

        {/* Durum Checkbox Dropdown */}
        <div className="ep-filter-dropdown" ref={durumDropdownRef}>
          <button className={`ep-filter-dropdown-btn ${filterDurumlar.length > 0 ? 'has-selection' : ''}`} onClick={() => { setDurumDropdownOpen(!durumDropdownOpen); setTurDropdownOpen(false); }}>
            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
            {filterDurumlar.length === 0 ? t('label.tumDurumlar', 'Tüm Durumlar') : `${filterDurumlar.length} ${t('emlak.durumSecili', 'Durum Seçili')}`}
            <svg className={`ep-filter-arrow ${durumDropdownOpen ? 'open' : ''}`} width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
          {durumDropdownOpen && (
            <div className="ep-filter-dropdown-menu">
              {Object.entries(durumLabels).map(([key, label]) => (
                <label key={key} className="ep-filter-checkbox-item">
                  <input type="checkbox" checked={filterDurumlar.includes(key)} onChange={() => toggleFilter(filterDurumlar, key, setFilterDurumlar)} />
                  <span className="ep-filter-checkmark"></span>
                  {t('emlak.durum.' + key, label)}
                </label>
              ))}
              {filterDurumlar.length > 0 && (
                <button className="ep-filter-clear-btn" onClick={() => setFilterDurumlar([])}>{t('btn.temizle')}</button>
              )}
            </div>
          )}
        </div>

        {/* Aktif/Pasif Filtresi */}
        <select className="ep-filter-select" value={filterAktif} onChange={e => setFilterAktif(e.target.value)}>
          <option value="">{t('label.tumu')}</option>
          <option value="aktif">{t('durum.aktif')}</option>
          <option value="pasif">{t('durum.pasif')}</option>
        </select>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="ep-loading">
          <i className="fas fa-spinner fa-spin"></i> {t('msg.yukleniyor', 'Yükleniyor…')}
        </div>
      ) : filtered.length === 0 ? (
        <div className="ep-empty">
          <i className="fas fa-home"></i>
          <p>{t('emlak.ilanYok', 'Henüz ilan eklenmemiş.')}</p>
        </div>
      ) : (
        <>
          <div className="ep-grid">
            {paginatedData.map(portfoy => {
              const vitrinFoto = getVitrinFoto(portfoy);
              return (
                <div key={portfoy.portfoyID} className="ep-card">
                  <div className="ep-card-img" onClick={() => openQuickView(portfoy.portfoyID)}>
                    {vitrinFoto ? (
                      <img src={`${axiosInstance.defaults.baseURL}/api/EmlakPortfoy/foto/${vitrinFoto}`} alt={portfoy.baslik} />
                    ) : (
                      <div className="ep-card-no-img">
                        <i className="fas fa-image"></i>
                      </div>
                    )}
                    <div className="ep-card-badges">
                      <span className="ep-badge ep-badge-glass ep-badge-overlay">
                        {t('emlak.durum.' + portfoy.durum, durumLabels[portfoy.durum] || portfoy.durum)}
                      </span>
                      <span className="ep-badge ep-badge-glass ep-badge-overlay ep-badge-tur">
                        {t('emlak.tur.' + portfoy.tur, turLabels[portfoy.tur] || portfoy.tur)}
                      </span>
                    </div>
                    {portfoy.fotograflar.length > 1 && (
                      <span className="ep-card-foto-count">
                        <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                        {portfoy.fotograflar.length}
                      </span>
                    )}
                  </div>
                  <div className="ep-card-body">
                    <div className="ep-card-content">
                      <div className="ep-card-price" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {portfoy.fiyat.toLocaleString('tr-TR')} ₺
                      </div>
                      <h3 className="ep-card-title" onClick={() => openQuickView(portfoy.portfoyID)}>
                        {portfoy.baslik}
                      </h3>
                      {portfoy.adres && (
                        <p className="ep-card-address">
                          <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                          {portfoy.adres}
                        </p>
                      )}
                      <div className="ep-card-meta">
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                          <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
                          {portfoy.m2} m²
                        </span>
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                          <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>
                          {portfoy.ilanNo}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="ep-card-footer">
                    <button className="ep-footer-btn" onClick={() => openQuickView(portfoy.portfoyID)} title="Hızlı Gör">
                      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    </button>
                    <button className="ep-footer-btn" onClick={() => navigate(`/emlak-portfoy/${portfoy.portfoyID}`)} title={t('btn.detay')}>
                      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                    </button>
                    <button className="ep-footer-btn ep-footer-btn-edit" onClick={() => navigate(`/emlak-portfoy/duzenle/${portfoy.portfoyID}`)} title={t('btn.duzenle')}>
                      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="ep-pagination">
              <button disabled={currentPage === 1} onClick={() => setCurrentPage(prev => prev - 1)}>
                <i className="fas fa-chevron-left"></i>
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button key={page} className={currentPage === page ? 'active' : ''} onClick={() => setCurrentPage(page)}>
                  {page}
                </button>
              ))}
              <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(prev => prev + 1)}>
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </>
      )}

      {/* Quick View Modal */}
      {qvPortfoy && quickViewIndex !== null && (
        <div className="ep-modal-overlay ep-qv-overlay" onClick={() => setQuickViewIndex(null)}>
          <div className="ep-qv-modal" onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="ep-qv-header">
              <div className="ep-qv-header-left">
                <span className="ep-qv-counter">{quickViewIndex + 1} / {filtered.length}</span>
                <h2 className="ep-qv-title">{qvPortfoy.baslik}</h2>
              </div>
              <div className="ep-qv-header-right">
                <button className="ep-qv-header-btn ep-qv-nav-btn" onClick={() => qvNavigate(-1)} disabled={quickViewIndex === 0} title={t('emlak.oncekiIlan', 'Önceki İlan')}>
                  <i className="fas fa-chevron-left"></i>
                </button>
                <button className="ep-qv-header-btn ep-qv-nav-btn" onClick={() => qvNavigate(1)} disabled={quickViewIndex === filtered.length - 1} title={t('emlak.sonrakiIlan', 'Sonraki İlan')}>
                  <i className="fas fa-chevron-right"></i>
                </button>
                <span className="ep-qv-header-sep"></span>
                <button className="ep-qv-header-btn" onClick={() => navigate(`/emlak-portfoy/${qvPortfoy.portfoyID}`)} title={t('emlak.detaySayfasi', 'Detay Sayfası')}>
                  <i className="fas fa-external-link-alt"></i>
                </button>
                <button className="ep-qv-header-btn" onClick={() => navigate(`/emlak-portfoy/duzenle/${qvPortfoy.portfoyID}`)} title={t('btn.duzenle')}>
                  <i className="fas fa-edit"></i>
                </button>
                <button className="ep-qv-header-btn ep-qv-close" onClick={() => setQuickViewIndex(null)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="ep-qv-body">
              {/* Sol: Galeri */}
              <div className="ep-qv-gallery">
                {qvPortfoy.fotograflar.length > 0 ? (
                  <>
                    <div className="ep-qv-gallery-main">
                      <img src={fotoUrl(qvPortfoy.fotograflar[qvActivePhoto].dosyaYolu)} alt={qvPortfoy.baslik} />
                      {qvPortfoy.fotograflar.length > 1 && (
                        <>
                          <button className="ep-gallery-nav ep-gallery-nav-prev" onClick={() => setQvActivePhoto(prev => prev === 0 ? qvPortfoy.fotograflar.length - 1 : prev - 1)}>
                            <i className="fas fa-chevron-left"></i>
                          </button>
                          <button className="ep-gallery-nav ep-gallery-nav-next" onClick={() => setQvActivePhoto(prev => prev === qvPortfoy.fotograflar.length - 1 ? 0 : prev + 1)}>
                            <i className="fas fa-chevron-right"></i>
                          </button>
                        </>
                      )}
                    </div>
                    {qvPortfoy.fotograflar.length > 1 && (
                      <div className="ep-gallery-thumbs">
                        {qvPortfoy.fotograflar.map((foto, idx) => (
                          <div key={foto.fotoID} className={`ep-gallery-thumb ${idx === qvActivePhoto ? 'active' : ''}`} onClick={() => setQvActivePhoto(idx)}>
                            <img src={fotoUrl(foto.dosyaYolu)} alt="" />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="ep-no-gallery">
                    <i className="fas fa-image"></i>
                    <p>{t('emlak.fotografEklenmemis', 'Fotoğraf eklenmemiş')}</p>
                  </div>
                )}
              </div>

              {/* Sağ: Bilgiler */}
              <div className="ep-qv-info">
                <div className="ep-qv-price">{qvPortfoy.fiyat.toLocaleString('tr-TR')} ₺</div>
                <div className="ep-qv-price-m2">{qvPortfoy.m2 > 0 ? `${Math.round(qvPortfoy.fiyat / qvPortfoy.m2).toLocaleString('tr-TR')} ₺/m²` : ''}</div>

                <div className="ep-qv-badges">
                  <span className={`ep-badge ep-badge-${qvPortfoy.durum.toLowerCase()}`}>{t('emlak.durum.' + qvPortfoy.durum, durumLabels[qvPortfoy.durum] || qvPortfoy.durum)}</span>
                  <span className={`ep-badge ep-badge-tur ep-badge-${qvPortfoy.tur.toLowerCase()}`}>{t('emlak.tur.' + qvPortfoy.tur, turLabels[qvPortfoy.tur] || qvPortfoy.tur)}</span>
                  {!qvPortfoy.aktifMi && <span className="ep-badge ep-badge-pasif">{t('durum.pasif')}</span>}
                </div>

                <div className="ep-qv-details">
                  <div className="ep-info-item">
                    <span className="ep-info-label"><i className="fas fa-ruler-combined"></i> {t('emlak.alan', 'Alan')}</span>
                    <span className="ep-info-value">{qvPortfoy.m2} m²</span>
                  </div>
                  <div className="ep-info-item">
                    <span className="ep-info-label"><i className="fas fa-hashtag"></i> {t('emlak.ilanNo', 'İlan No')}</span>
                    <span className="ep-info-value">{qvPortfoy.ilanNo}</span>
                  </div>
                  {qvPortfoy.adres && (
                    <div className="ep-info-item">
                      <span className="ep-info-label"><i className="fas fa-map-marker-alt"></i> {t('label.adres', 'Adres')}</span>
                      <span className="ep-info-value">{qvPortfoy.adres}</span>
                    </div>
                  )}
                  <div className="ep-info-item">
                    <span className="ep-info-label"><i className="fas fa-calendar"></i> {t('emlak.eklenme', 'Eklenme')}</span>
                    <span className="ep-info-value">{formatDate(qvPortfoy.eklenmeTarihi)}</span>
                  </div>
                </div>

                {qvPortfoy.ozellikler.length > 0 && (
                  <div className="ep-qv-section">
                    <h3 className="ep-qv-section-title"><i className="fas fa-tags"></i> {t('emlak.ozellikler', 'Özellikler')}</h3>
                    <div className="ep-ozellik-tags">
                      {qvPortfoy.ozellikler.map(oz => (
                        <span key={oz.ozellikID} className="ep-ozellik-tag"><i className="fas fa-check"></i> {oz.ozellikAdi}</span>
                      ))}
                    </div>
                  </div>
                )}

                {qvPortfoy.iletisimler.length > 0 && (
                  <div className="ep-qv-section">
                    <h3 className="ep-qv-section-title"><i className="fas fa-address-book"></i> {t('emlak.ilgiliKisiler', 'İlgili Kişiler')}</h3>
                    {qvPortfoy.iletisimler.map(kisi => (
                      <div key={kisi.iletisimID} className="ep-qv-kisi">
                        <strong>{kisi.adSoyad}</strong>
                        {kisi.telefon && <span><i className="fas fa-phone"></i> {kisi.telefon}</span>}
                        {kisi.eposta && <span><i className="fas fa-envelope"></i> {kisi.eposta}</span>}
                      </div>
                    ))}
                  </div>
                )}

                {qvPortfoy.aciklama && (
                  <div className="ep-qv-section">
                    <h3 className="ep-qv-section-title"><i className="fas fa-align-left"></i> {t('label.aciklama', 'Açıklama')}</h3>
                    <div className="ep-qv-aciklama" dangerouslySetInnerHTML={{ __html: qvPortfoy.aciklama }} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="ep-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="ep-modal" onClick={e => e.stopPropagation()}>
            <div className="ep-modal-header">
              <span className="ep-modal-title"><i className="fas fa-exclamation-triangle" style={{ color: '#dc2626', marginRight: 8 }}></i>{t('emlak.ilaniSil', 'İlanı Sil')}</span>
              <button className="ep-modal-close" onClick={() => setDeleteConfirm(null)}>✕</button>
            </div>
            <div className="ep-modal-body">
              <p>{t('emlak.ilaniSilOnay', 'Bu ilanı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.')}</p>
            </div>
            <div className="ep-modal-footer">
              <button className="ep-btn-cancel" onClick={() => setDeleteConfirm(null)}>{t('btn.iptal')}</button>
              <button className="ep-btn-danger" onClick={() => handleDelete(deleteConfirm)}>
                <i className="fas fa-trash"></i> {t('btn.sil', 'Sil')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmlakPortfoyListesi;
